import type { QueryClient } from '@tanstack/react-query';
import { SessionService } from '@/services/session.service';

export const RECONCILE_VIDEO_ROOM_SUCCESS_MESSAGES: Record<
	Sessions.ReconcileVideoRoomStatus,
	string
> = {
	synced: 'Sala de vídeo já estava sincronizada com o provedor.',
	updated: 'Sala de vídeo atualizada no provedor.',
	created: 'Sala de vídeo criada e sincronizada com o provedor.',
};

/** Atualiza a sessão em todas as listas em cache (`useGetSessions`). */
export function upsertSessionInSessionsCache(
	queryClient: QueryClient,
	updatedSession: Sessions.Session,
) {
	queryClient.setQueriesData<Sessions.GetSessionsRespose>(
		{ queryKey: ['sessions'] },
		(previous) => {
			if (!previous?.data?.length) return previous;

			const index = previous.data.findIndex(
				(s) => s.id === updatedSession.id,
			);
			if (index === -1) return previous;

			const nextData = [...previous.data];
			nextData[index] = { ...nextData[index], ...updatedSession };

			return { ...previous, data: nextData };
		},
	);

	queryClient.setQueryData<Sessions.Session>(
		['session', updatedSession.id],
		(previous) =>
			previous ? { ...previous, ...updatedSession } : updatedSession,
	);
}

function matchesMeSessionsQueryKey(
	queryKey: readonly unknown[],
	session: Sessions.Session,
): boolean {
	if (queryKey[0] !== 'sessions' || queryKey[1] !== true) return false;

	const status = queryKey[3] as Sessions.SessionStatus | undefined;
	const statusList = queryKey[4] as Sessions.SessionStatus[] | undefined;

	if (status && session.status !== status) return false;
	if (
		Array.isArray(statusList) &&
		statusList.length > 0 &&
		!statusList.includes(session.status)
	) {
		return false;
	}

	return true;
}

/** Inclui sessão nova nas listas `isMe` em cache (ex.: home sem F5). */
export function prependSessionToMeSessionsCache(
	queryClient: QueryClient,
	session: Sessions.Session,
) {
	queryClient.setQueriesData<Sessions.GetSessionsRespose>(
		{
			queryKey: ['sessions'],
			predicate: (query) =>
				matchesMeSessionsQueryKey(query.queryKey, session),
		},
		(previous) => {
			if (previous?.data?.some((item) => item.id === session.id)) {
				return previous;
			}

			if (!previous) {
				return { data: [session], total: 1 };
			}

			return {
				...previous,
				data: [session, ...previous.data],
				total: (previous.total ?? previous.data.length) + 1,
			};
		},
	);

	queryClient.setQueryData<Sessions.Session>(
		['session', session.id],
		session,
	);
}

export async function invalidateSessionsQueries(queryClient: QueryClient) {
	await queryClient.invalidateQueries({ queryKey: ['sessions'] });
}

export async function invalidateSessionVideoRoomQueries(
	queryClient: QueryClient,
	sessionId: string,
) {
	await queryClient.invalidateQueries({ queryKey: ['session', sessionId] });
	await queryClient.invalidateQueries({
		queryKey: ['session-video-room', sessionId],
	});
}

/** Sincroniza sala Daily após mudança de horário/data (sessão online). */
export async function reconcileSessionVideoRoom(
	queryClient: QueryClient,
	sessionId: string,
): Promise<Sessions.ReconcileVideoRoomResponse> {
	const result = await SessionService.reconcileVideoRoom(sessionId);
	await invalidateSessionsQueries(queryClient);
	await invalidateSessionVideoRoomQueries(queryClient, sessionId);
	return result;
}
